import PropTypes from 'prop-types';
import { useMemo } from 'react';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { Box, Chip, Typography } from '@mui/material';
import {
  IconBell,
  IconBellOff,
  IconFileCheck,
  IconFilePlus,
  IconFilePencil,
  IconCheck,
  IconX,
  IconHelp,
  IconExclamationCircle,
  IconFileOff
} from '@tabler/icons';
import { useTimeAgo } from './utils/timeAgo';
import { useSessionUser } from '../ProfileSection/userInfo';

const toStr = (v) => (v == null ? null : String(v));

// reference.data is a specification (has the approval fields)
const hasSpecData = (d) => Boolean(d) && typeof d === 'object' && ('supervisor_id' in d || 'supervisorConfirmed' in d);

function parseMaybeArrayString(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  if (typeof input !== 'string') return [];

  const s = input.trim();

  if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('{') && s.endsWith('}'))) {
    try {
      const json = JSON.parse(s);
      return Array.isArray(json) ? json : [json];
    } catch {
      try {
        const json = JSON.parse(s.replace(/'/g, '"'));
        return Array.isArray(json) ? json : [json];
      } catch {
        // fall through
      }
    }
  }

  return s
    .replace(/^\[|\]$/g, '')
    .split(',')
    .map((x) => x.trim().replace(/^["'`]|["'`]$/g, ''))
    .filter(Boolean);
}

function parseArrayMaybeDoubleEncoded(input) {
  let v = input;
  for (let i = 0; i < 2 && typeof v === 'string'; i++) {
    try {
      v = JSON.parse(v);
    } catch {
      break;
    }
  }
  return Array.isArray(v) ? v : [];
}

const getMyAdditionalConfirmation = (spec, meId) => {
  const arr = parseMaybeArrayString(spec?.additionalSupervisors);
  if (!Array.isArray(arr) || arr.length === 0) return false;
  const meIdStr = toStr(meId);
  for (const item of arr) {
    if (item && typeof item === 'object' && toStr(item.id) === meIdStr) return Boolean(item.confirmed);
  }
  return false;
};

const getMyAdditionalRejection = (spec, meId) => {
  const arr = parseArrayMaybeDoubleEncoded(spec?.additionalSupervisorRejected);
  if (!Array.isArray(arr) || arr.length === 0) return false;
  const meIdStr = toStr(meId);
  for (const item of arr) {
    if (item && typeof item === 'object' && toStr(item.id) === meIdStr) {
      return Boolean(
        item.declined === true ||
          item.declined === 'true' ||
          item.declined === 1 ||
          item.executive_rejected === 1 ||
          item.executive_rejected === true ||
          item.executive_rejected === 'true'
      );
    }
  }
  return false;
};

// Same rules as before: has *this user* confirmed / declined the item?
const getMyDecision = (n, me) => {
  const type = n?.NotificationType?.name;
  const data = n?.reference?.data;
  if (!me) return { confirmed: false, declined: false };

  // a renunciation's reference is the order; the server sends that order's specification as reference.data
  const specDecision = type === 'new_specification_inserted' || type === 'new_specification_updated' || (type === 'service_renunciated' && hasSpecData(data));
  if (specDecision) {
    const isMainSupervisor = toStr(me.id) === toStr(data?.supervisor_id);
    const confirmed = isMainSupervisor ? Boolean(data?.supervisorConfirmed) : getMyAdditionalConfirmation(data, me.id);
    if (confirmed) return { confirmed: true, declined: false };
    const declined = isMainSupervisor ? Boolean(data?.supervisorRejected) : getMyAdditionalRejection(data, me.id);
    return { confirmed: false, declined: Boolean(declined) };
  }
  if (type === 'new_contract_submitted_for_review') {
    return { confirmed: Boolean(data?.executive_confirmed), declined: Boolean(data?.executive_rejected) };
  }
  return { confirmed: false, declined: false };
};

// icon + title per notification type
const TYPE_META = {
  new_service_order_request_waiting_for_review: { icon: IconExclamationCircle, title: 'Naujos paslaugos užsakymo užklausai reikalinga peržiūra' },
  new_service_order_request: { icon: IconHelp, title: 'Nauja paslaugos užsakymo užklausa' },
  new_specification_inserted: { icon: IconFilePlus, title: 'Pridėta nauja specifikacija' },
  supervisor_confirmed_specification: { icon: IconFileCheck, title: 'Specifikacija patvirtinta', tone: 'approved' },
  new_contract_submitted_for_review: { icon: IconFilePencil, title: 'Sutartis laukia patvirtinimo' },
  executive_confirmed_contract: { icon: IconCheck, title: 'Sutartis buvo patvirtinta', tone: 'approved' },
  executive_rejected_contract: { icon: IconX, title: 'Sutartis buvo atmesta', tone: 'rejected' },
  supervisor_declined_specification: { icon: IconX, title: 'Specifikacija buvo atmesta', tone: 'rejected' },
  new_specification_updated: { icon: IconFilePencil, title: 'Paslaugos keitimas laukia patvirtinimo' },
  service_renunciated: { icon: IconFileOff, title: 'Paslaugos atsisakymas laukia patvirtinimo' }
};

// ----- service_renunciated: reference_id is the order (Orders.id) -----
// Organizations -> contracts -> Services (contract_id, order_id, specification_id): the Services row with this order
// gives the specification to review, so the click opens the same review page as a new specification.
const sameId = (a, b) => a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);

const findServiceRow = (node, orderId, depth = 0) => {
  if (!node || typeof node !== 'object' || depth > 9) return null;
  if (!Array.isArray(node) && (sameId(node.order_id, orderId) || sameId(node.orderId, orderId)) && (node.specification_id !== undefined || node.contract_id !== undefined)) return node;
  for (const v of Array.isArray(node) ? node : Object.values(node)) {
    const hit = v && typeof v === 'object' ? findServiceRow(v, orderId, depth + 1) : null;
    if (hit) return hit;
  }
  return null;
};

// -> { orderId, specificationId, contractId, serviceId, organization } or throws with a Lithuanian message
export const resolveRenouncedOrder = async (n) => {
  const data = n?.reference?.data;
  const orderId = n?.reference_id ?? data?.order_id;
  if (orderId === undefined || orderId === null) throw new Error('Pranešime nėra užsakymo numerio.');

  // the server already sent this order's specification (reference.data): nothing to look up
  if (hasSpecData(data) && data.id !== undefined) {
    const org = data.Organization || n?.reference?.Organization;
    return {
      orderId,
      specificationId: data.id,
      contractId: data.contract_id,
      serviceId: data.services_id,
      organization: org?.original_name || org?.name,
      organizationName: org?.name
    };
  }

  const res = await axios.get('/api/db/getAllOrganizationsData', { params: { tc_ids: [], organizationTypes: [], servicesStatuses: [], municipalities: [] } });
  const orgs = Array.isArray(res.data?.result) ? res.data.result : [];
  for (const org of orgs) {
    const row = findServiceRow(org?.contract, orderId);
    if (row) {
      if (row.specification_id === undefined || row.specification_id === null) throw new Error('Šiam užsakymui nerasta specifikacija.');
      return {
        orderId,
        specificationId: row.specification_id,
        contractId: row.contract_id,
        serviceId: row.id,
        organization: org.original_name || org.name, // as the order document expects it
        organizationName: org.name // as the organization pages expect it
      };
    }
  }
  console.warn('service_renunciated: no Services row with order_id', orderId);
  throw new Error('Nepavyko rasti užsakymo, kurio paslaugos atsisakyta.');
};

// opens the specification review page for a service_renunciated notification
export const openRenouncedOrder = async (n, navigate) => {
  const order = await resolveRenouncedOrder(n);
  navigate(`/peržiūrėti/specification/${order.specificationId}`, { state: { order, notification: n } });
};

// status chips (same labels and rules as before)
const getChips = (n, decision) => {
  const type = n?.NotificationType?.name;
  const chips = [];

  const decides =
    type === 'new_specification_inserted' ||
    type === 'new_contract_submitted_for_review' ||
    type === 'new_specification_updated' ||
    (type === 'service_renunciated' && hasSpecData(n?.reference?.data));
  if (decides) {
    if (decision.confirmed) chips.push({ label: 'Patvirtinta', tone: 'approved' });
    else if (decision.declined) chips.push({ label: 'Atmesta', tone: 'rejected' });
    else chips.push({ label: 'Nepatvirtinta', tone: 'pending' });
  }
  if (type === 'supervisor_declined_specification') {
    chips.push({ label: 'Atmesta', tone: 'rejected' });
  }
  if (type === 'new_service_order_request_waiting_for_review') {
    if (n.reference?.data?.tc_id === null || n?.tc_id === null) chips.push({ label: 'Reikia priskirti TC', tone: 'pending' });
    else chips.push({ label: 'Užklausa priskirta TC', tone: 'approved' });
  }
  if (type === 'new_service_order_request') {
    if (n.reference?.data?.orderId === null || n?.orderId) chips.push({ label: 'Reikia pateikti specifikaciją', tone: 'pending' });
    else chips.push({ label: 'Užsakymas priimtas', tone: 'approved' });
  }
  return chips;
};

const NotificationRow = ({ n = {}, onItemClick, me }) => {
  const theme = useTheme();
  const when = useTimeAgo(n?.createdAt);
  const status = theme.palette.custom.status;

  const decision = useMemo(() => getMyDecision(n, me), [n, me]);
  const chips = getChips(n, decision);

  const type = n?.NotificationType?.name;
  const meta = TYPE_META[type] || { icon: IconBell, title: n.title || 'Pranešimas' };
  const Icon = meta.icon;
  const unread = !n.is_read && !n.read;

  // icon tile colour: the item's state, or neutral
  const tone = meta.tone || chips[0]?.tone;
  const tile = tone ? status[tone] : { bg: theme.palette.action.hover, fg: theme.palette.text.secondary };

  const open = () => {
    if (typeof onItemClick === 'function') onItemClick(n);
  };

  return (
    <Box
      component="li"
      role="button"
      tabIndex={0}
      aria-label={`${unread ? 'Neperskaitytas: ' : ''}${meta.title}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      sx={{
        listStyle: 'none',
        position: 'relative',
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        bgcolor: unread ? theme.palette.custom.sel : 'transparent',
        borderBottom: 1,
        borderColor: 'divider',
        transition: 'background-color .15s ease',
        '&:last-of-type': { borderBottom: 0 },
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': { outline: 'none', boxShadow: `inset 0 0 0 2px ${theme.palette.primary[200]}` }
      }}
    >
      {unread && (
        <Box
          aria-hidden="true"
          sx={{ position: 'absolute', left: 6, top: 28, width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }}
        />
      )}
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: tile.bg,
          color: tile.fg
        }}
      >
        <Icon stroke={1.8} size={18} />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ flexGrow: 1, fontSize: 13.5, lineHeight: 1.35, fontWeight: unread ? 700 : 500, color: 'text.primary' }}>
            {meta.title}
          </Typography>
          <Typography sx={{ flexShrink: 0, fontSize: 11.5, color: 'text.disabled', whiteSpace: 'nowrap' }}>{when}</Typography>
        </Box>
        {n.subtitle && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{n.subtitle}</Typography>}
        {n.message && (
          <Typography
            sx={{
              fontSize: 12.5,
              color: 'text.secondary',
              mt: 0.25,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {n.message}
          </Typography>
        )}
        {chips.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {chips.map((c) => (
              <Chip
                key={c.label}
                label={c.label}
                size="small"
                sx={{ height: 22, fontSize: 11.5, bgcolor: status[c.tone].bg, color: status[c.tone].fg, '& .MuiChip-label': { px: '8px' } }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

NotificationRow.propTypes = {
  n: PropTypes.object,
  onItemClick: PropTypes.func,
  me: PropTypes.object
};

// user: the signed-in user (passed in so the list doesn't load it once per row)
export default function NotificationList({ notifications = [], onItemClick = () => {}, user }) {
  const me = useSessionUser(user);

  const list = Array.isArray(notifications)
    ? notifications
    : Array.isArray(notifications?.notifications)
    ? notifications.notifications
    : [];

  if (list.length === 0) {
    return (
      <Box sx={{ py: 5, px: 3, textAlign: 'center', color: 'text.disabled' }}>
        <IconBellOff stroke={1.6} size={28} />
        <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 600, color: 'text.primary' }}>Jūs neturite pranešimų</Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Viską peržiūrėjote.</Typography>
      </Box>
    );
  }

  return (
    <Box component="ul" sx={{ m: 0, p: 0 }}>
      {list.map((n, i) => (
        <NotificationRow key={n?.id ?? i} n={n} onItemClick={onItemClick} me={me} />
      ))}
    </Box>
  );
}

NotificationList.propTypes = {
  notifications: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onItemClick: PropTypes.func,
  user: PropTypes.object
};